import { Truck, AlertTriangle, Banknote, Upload, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import ContractPickerModal from '@/components/shared/contract-picker-modal';
import { Card } from '@/components/ui/card';
import usePageTitle from '@/hooks/use-page-title';
import type { Contract } from '@/types/api/contract';

const linkActions = [
  {
    key: 'sinistre',
    label: 'Je déclare un sinistre',
    route: '/mes-sinistres/je-declare-un-sinistre',
    icon: AlertTriangle,
    color: 'bg-destructive/10 text-destructive',
  },
  {
    key: 'rib',
    label: 'Je change de RIB',
    route: '/mon-profil/je-change-mes-informations-bancaires',
    icon: Banknote,
    color: 'bg-accent/10 text-accent',
  },
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bonjour';
  if (hour < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

export interface HomePWAProps {
  firstName?: string;
  lastName?: string;
  titre?: string;
  contracts?: Contract[];
  isWakam?: boolean;
}

interface ActionCardProps {
  icon: LucideIcon;
  label: string;
  color: string;
}

const HomePWA = ({ firstName, lastName, titre, contracts = [], isWakam }: HomePWAProps) => {
  const displayName = titre === 'SOC' ? lastName : firstName;
  usePageTitle('Accueil');
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerContracts, setPickerContracts] = useState<Contract[]>([]);
  const [pickerAction, setPickerAction] = useState<'demenage' | 'document' | null>(null);

  const demenageContracts = contracts.filter(c => !isWakam && c.situation?.statutCode === "1" && c.vehicule?.codeProduit !== '7311_QMCO' && c.vehicule?.codeProduit !== '7311_2MCO');

  const handleDemenageClick = () => {
    if (demenageContracts.length === 1) {
      navigate(`/mes-contrats/mon-contrat/${demenageContracts[0].id}/je-demenage`);
      return;
    }
    setPickerAction('demenage');
    setPickerContracts(demenageContracts);
    setPickerOpen(true);
  };

  const handleDocumentClick = () => {
    let filteredContracts = contracts;
    const withPieces = contracts.filter(c => (c.nb_pieces ?? 0) > 0);
    if (withPieces.length > 0) filteredContracts = withPieces;

    if (filteredContracts.length === 1) {
      window.open(filteredContracts[0].upload_app_link, '_blank');
      return;
    }

    setPickerAction('document');
    setPickerContracts(filteredContracts);
    setPickerOpen(true);
  };

  const handleContractSelect = (contract: Contract) => {
    if (pickerAction === 'demenage') {
      navigate(`/mes-contrats/mon-contrat/${contract.id}/je-demenage`);
    } else {
      window.open(contract.upload_app_link, '_blank');
    }
  };

  const ActionCard = ({ icon: Icon, label, color }: ActionCardProps) => (
    <Card className="flex flex-col items-center justify-center gap-3 p-6 h-full hover:shadow-md transition-shadow cursor-pointer">
      <div className={`flex items-center justify-center w-14 h-14 rounded-full ${color}`}>
        <Icon className="w-8 h-8" aria-hidden="true" focusable="false" />
      </div>
      <span className="text-sm font-medium text-center text-card-foreground">{label}</span>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div data-tutorial="pwa-greeting">
        <h1 className="text-2xl font-bold">
          {getGreeting()}, {displayName}&nbsp;!
        </h1>
        <p className="text-muted-foreground mt-1">Que souhaitez faire ?</p>
      </div>

      <div data-tutorial="pwa-actions" className="grid grid-cols-2 gap-4">
        {demenageContracts.length > 0 && (
          <button
            type="button"
            onClick={handleDemenageClick}
            className="text-left"
            {...(demenageContracts.length > 1 ? { 'aria-haspopup': 'dialog', 'aria-expanded': pickerOpen && pickerAction === 'demenage' } : {})}
          >
            <ActionCard icon={Truck} label="Je change d'adresse" color="bg-primary/10 text-primary" />
          </button>
        )}
        {linkActions.map(({ key, label, route, icon, color }) => (
          <Link key={key} to={route} className="no-underline">
            <ActionCard icon={icon} label={label} color={color} />
          </Link>
        ))}
        <button
          type="button"
          onClick={handleDocumentClick}
          className="text-left"
          aria-haspopup="dialog"
          aria-expanded={pickerOpen && pickerAction === 'document'}
        >
          <ActionCard icon={Upload} label="J'envoie des documents" color="bg-success/10 text-success" />
        </button>
      </div>

      <ContractPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleContractSelect}
        contracts={pickerContracts}
      />
    </div>
  );
};

export default HomePWA;
